import type { DocumentViewportValue } from "@/features/document-viewport/types";
import {
  computeMeasurementValue,
  formatMeasurementValue,
  type Point,
} from "@/lib/geometry";
import {
  MEASUREMENT_GEOMETRY_TYPES,
  type MeasurementAnnotation,
  type MeasurementGeometry,
  type PolygonGeometry,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import {
  MEASUREMENT_TYPES,
  TOOL_IDS,
  type MeasurementType,
  type ToolType,
} from "@/viewport-tools/measurement";

export function hexToRgba(hex: string, alpha: number) {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized;
  const red = Number.parseInt(expanded.slice(0, 2), 16);
  const green = Number.parseInt(expanded.slice(2, 4), 16);
  const blue = Number.parseInt(expanded.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export function darkenHex(hex: string, amount: number) {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized;
  const clamp = (value: number) => Math.max(0, Math.min(255, value));
  const red = clamp(Math.round(Number.parseInt(expanded.slice(0, 2), 16) * (1 - amount)));
  const green = clamp(Math.round(Number.parseInt(expanded.slice(2, 4), 16) * (1 - amount)));
  const blue = clamp(Math.round(Number.parseInt(expanded.slice(4, 6), 16) * (1 - amount)));
  return `rgb(${red}, ${green}, ${blue})`;
}

export function getScreenBoundsForGeometry(
  geometry: MeasurementGeometry | PolygonGeometry,
  viewport: DocumentViewportValue
) {
  if (geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    return viewport.transforms.pageRectToScreenRect({
      x: geometry.x,
      y: geometry.y,
      width: geometry.width,
      height: geometry.height,
    });
  }

  if (geometry.vertices.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const screenVertices = geometry.vertices.map((vertex) =>
    viewport.transforms.pageToScreenPoint(vertex)
  );
  const xs = screenVertices.map((vertex) => vertex.x);
  const ys = screenVertices.map((vertex) => vertex.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function normalizeRect(startPoint: Point, currentPoint: Point): MeasurementGeometry {
  return {
    type: MEASUREMENT_GEOMETRY_TYPES.rect,
    x: Math.min(startPoint.x, currentPoint.x),
    y: Math.min(startPoint.y, currentPoint.y),
    width: Math.abs(currentPoint.x - startPoint.x),
    height: Math.abs(currentPoint.y - startPoint.y),
  };
}

export function measurementRequiresHeight(
  tool: ToolType,
  measurementType: MeasurementType
): boolean {
  return (
    measurementType === MEASUREMENT_TYPES.CF ||
    ((tool === TOOL_IDS.line || tool === TOOL_IDS.polyline) &&
      measurementType === MEASUREMENT_TYPES.SQFT)
  );
}

export function translateGeometry(
  geometry: MeasurementGeometry | PolygonGeometry,
  dx: number,
  dy: number
): MeasurementGeometry | PolygonGeometry {
  if (geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    return {
      ...geometry,
      x: geometry.x + dx,
      y: geometry.y + dy,
    };
  }

  return {
    type: MEASUREMENT_GEOMETRY_TYPES.poly,
    vertices: geometry.vertices.map((vertex) => ({
      x: vertex.x + dx,
      y: vertex.y + dy,
    })),
  };
}

export function getMeasurementGeometryBounds(
  geometry: MeasurementGeometry | PolygonGeometry
): { minX: number; minY: number; maxX: number; maxY: number } {
  if (geometry.type === MEASUREMENT_GEOMETRY_TYPES.rect) {
    return {
      minX: geometry.x,
      minY: geometry.y,
      maxX: geometry.x + geometry.width,
      maxY: geometry.y + geometry.height,
    };
  }

  if (geometry.vertices.length === 0) {
    return {
      minX: 0,
      minY: 0,
      maxX: 0,
      maxY: 0,
    };
  }

  const xs = geometry.vertices.map((vertex) => vertex.x);
  const ys = geometry.vertices.map((vertex) => vertex.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

export function getMeasurementLabelAnchor(
  geometry: MeasurementGeometry | PolygonGeometry
): Point {
  const bounds = getMeasurementGeometryBounds(geometry);
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };
}

export function getMeasurementLabelPosition(
  geometry: MeasurementGeometry | PolygonGeometry,
  annotation?: MeasurementAnnotation
): Point {
  const anchor = getMeasurementLabelAnchor(geometry);
  return {
    x: anchor.x + (annotation?.textOffset?.x ?? 0),
    y: anchor.y + (annotation?.textOffset?.y ?? 0),
  };
}

export function getMeasurementLeaderAnchor(
  geometry: MeasurementGeometry | PolygonGeometry,
  leaderTarget: Point,
  options?: { closed?: boolean }
): Point {
  const bounds = getMeasurementGeometryBounds(geometry);
  const center = {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
  };

  if (geometry.type === MEASUREMENT_GEOMETRY_TYPES.poly) {
    return (
      getClosestSegmentPoint(geometry.vertices, leaderTarget, options?.closed ?? false) ?? center
    );
  }

  const corners = [
    { x: geometry.x, y: geometry.y },
    { x: geometry.x + geometry.width, y: geometry.y },
    { x: geometry.x + geometry.width, y: geometry.y + geometry.height },
    { x: geometry.x, y: geometry.y + geometry.height },
  ];

  return getClosestSegmentPoint(corners, leaderTarget, true) ?? center;
}

export function getClosestSegmentInsertionPoint(
  vertices: Point[],
  target: Point,
  closed: boolean
): { segmentIndex: number; point: Point } | null {
  if (vertices.length < 2) {
    return null;
  }

  const segmentCount = closed ? vertices.length : vertices.length - 1;
  let closest: { segmentIndex: number; point: Point } | null = null;
  let closestDistanceSquared = Number.POSITIVE_INFINITY;

  for (let index = 0; index < segmentCount; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    const point = getClosestPointOnSegment(start, end, target);
    const distanceSquared = (point.x - target.x) ** 2 + (point.y - target.y) ** 2;

    if (distanceSquared < closestDistanceSquared) {
      closest = { segmentIndex: index, point };
      closestDistanceSquared = distanceSquared;
    }
  }

  return closest;
}

function getClosestSegmentPoint(
  vertices: Point[],
  target: Point,
  closed: boolean
): Point | null {
  if (vertices.length === 0) {
    return null;
  }

  if (vertices.length === 1) {
    return vertices[0];
  }

  const segmentCount = closed ? vertices.length : vertices.length - 1;
  let closestPoint: Point | null = null;
  let closestDistanceSquared = Number.POSITIVE_INFINITY;

  for (let index = 0; index < segmentCount; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    const point = getClosestPointOnSegment(start, end, target);
    const distanceSquared = (point.x - target.x) ** 2 + (point.y - target.y) ** 2;

    if (distanceSquared < closestDistanceSquared) {
      closestPoint = point;
      closestDistanceSquared = distanceSquared;
    }
  }

  return closestPoint;
}

function getClosestPointOnSegment(start: Point, end: Point, target: Point): Point {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared <= 0) {
    return start;
  }

  const t = Math.max(
    0,
    Math.min(1, ((target.x - start.x) * dx + (target.y - start.y) * dy) / lengthSquared)
  );

  return {
    x: start.x + dx * t,
    y: start.y + dy * t,
  };
}

export function measurementHasMovedLabel(annotation?: MeasurementAnnotation): boolean {
  const x = annotation?.textOffset?.x ?? 0;
  const y = annotation?.textOffset?.y ?? 0;
  return Math.abs(x) > 1e-6 || Math.abs(y) > 1e-6;
}

export function shouldShowMeasurementLeader(annotation?: MeasurementAnnotation): boolean {
  if (annotation?.leaderVisible !== undefined) {
    return annotation.leaderVisible;
  }
  return measurementHasMovedLabel(annotation);
}

export function areGeometriesEqual(
  first: MeasurementGeometry | PolygonGeometry,
  second: MeasurementGeometry | PolygonGeometry
): boolean {
  const epsilon = 1e-6;
  if (first.type !== second.type) {
    return false;
  }
  if (
    first.type === MEASUREMENT_GEOMETRY_TYPES.rect &&
    second.type === MEASUREMENT_GEOMETRY_TYPES.rect
  ) {
    return (
      Math.abs(first.x - second.x) <= epsilon &&
      Math.abs(first.y - second.y) <= epsilon &&
      Math.abs(first.width - second.width) <= epsilon &&
      Math.abs(first.height - second.height) <= epsilon
    );
  }
  if (
    first.type === MEASUREMENT_GEOMETRY_TYPES.poly &&
    second.type === MEASUREMENT_GEOMETRY_TYPES.poly
  ) {
    if (first.vertices.length !== second.vertices.length) {
      return false;
    }
    return first.vertices.every((vertex, index) => {
      const other = second.vertices[index];
      return (
        Math.abs(vertex.x - other.x) <= epsilon &&
        Math.abs(vertex.y - other.y) <= epsilon
      );
    });
  }
  return false;
}

export function computeValue(
  tool: ToolType,
  measurementType: MeasurementType,
  geometry: MeasurementGeometry | PolygonGeometry,
  height: number | undefined,
  pixelsPerFoot: number,
  pageAspectRatio: number = 1
): number {
  return computeMeasurementValue(
    tool,
    measurementType,
    geometry,
    height,
    pixelsPerFoot,
    pageAspectRatio
  );
}

export function formatDisplayedMeasurement(
  measurementType: MeasurementType,
  value: number
): string {
  return formatMeasurementValue(value, measurementType);
}

export function getMeasurementCardHeight(
  requiresHeight: boolean,
  hasInlineError: boolean
): number {
  const baseHeight = requiresHeight ? 244 : 206;
  return hasInlineError ? baseHeight + 34 : baseHeight;
}

export function getEditableValueLabel(measurementType: MeasurementType) {
  if (measurementType === MEASUREMENT_TYPES.LF) {
    return "Length";
  }
  if (measurementType === MEASUREMENT_TYPES.SQFT) {
    return "Area";
  }
  return "Volume";
}
